/**
 * Product Promo Modal
 * Opens a popup with collection products in the theme's native slideshow
 * when a promo badge is clicked in the cart drawer.
 *
 * The section "promo-popup-content" is fetched via the Shopify Sections API
 * and returns a fully-formed <slideshow-component> which self-initialises
 * as soon as it is inserted into the DOM (connectedCallback).
 */
class ProductPromoModal {
  constructor() {
    this.swat = null;
    this.#init();
    this.#initSwym();
  }

  getElements() {
    const modal = document.getElementById('product-promo-modal');
    if (!modal) return null;

    return {
      modal,
      titleEl: modal.querySelector('.product-promo-modal__title'),
      shopDealEl: modal.querySelector('.product-promo-modal__shop-deal'),
      contentEl: modal.querySelector('.product-promo-modal__content'),
      closeBtn: modal.querySelector('.product-promo-modal__close'),
      overlayEl: modal.querySelector('.product-promo-modal__overlay')
    };
  }

  /* ─── Private helpers ─────────────────────────────────────── */

  #initSwym() {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      this.swat = swat;
    });
  }

  #init() {
    // Delegate click on promo badge triggers (works for dynamically rendered carts)
    document.addEventListener('click', (e) => {
      const trigger = e.target?.closest('.js-promo-badge-trigger');
      if (trigger) {
        e.preventDefault();
        const handle = trigger.dataset.promoHandle;
        const title  = trigger.dataset.promoTitle || (trigger.querySelector('span') ?? trigger).textContent?.trim();
        const link   = trigger.getAttribute('href') || trigger.href;

        if (handle && title && link) {
          this.open(handle, title, link);
        } else {
          console.warn('[ProductPromoModal] Trigger element missing expected data attributes:', trigger);
        }
        return;
      }

      // Delegate click on close button
      const closeBtn = e.target?.closest('.product-promo-modal__close');
      if (closeBtn) {
        this.close();
        return;
      }

      // Delegate click on overlay
      const overlay = e.target?.closest('.product-promo-modal__overlay');
      if (overlay) {
        this.close();
        return;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const elements = this.getElements();
        if (elements && elements.modal && elements.modal.style.display === 'flex') {
          this.close();
        }
      }
    });
  }

  /* ─── Public API ──────────────────────────────────────────── */

  async open(handle, title, link) {
    const elements = this.getElements();
    if (!elements) return;

    const { modal, titleEl, shopDealEl, contentEl } = elements;
    if (!handle || !titleEl || !shopDealEl || !contentEl || !modal) return;

    // Update header
    titleEl.textContent = title;
    shopDealEl.setAttribute('href', link);

    // Show modal with loading state
    contentEl.innerHTML = '<div style="width:100%;text-align:center;padding:40px 0;">Loading&hellip;</div>';
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    try {
      const root        = window.Shopify?.routes?.root ?? '/';
      const response    = await fetch(`${root}collections/${handle}?sections=promo-popup-content`);
      const json        = await response.json();
      const sectionHtml = json['promo-popup-content'];

      // Re-query elements after dynamic fetch in case they changed
      const currentElements = this.getElements();
      if (!currentElements || !currentElements.contentEl) return;

      if (sectionHtml?.trim()) {
        // Strip the outer shopify-section wrapper so only the slideshow markup
        // lands inside product-promo-modal__content
        const temp           = document.createElement('div');
        temp.innerHTML       = sectionHtml;
        const inner          = temp.querySelector('.shopify-section');
        currentElements.contentEl.innerHTML = inner ? inner.innerHTML : sectionHtml;
        
        // Initialise Swym wishlist buttons if the app is present
        const swat = this.swat || window._swat;
        if (swat && typeof swat.initializeButtons === 'function') {
          setTimeout(() => {
            swat.initializeButtons();
          }, 100);
        }

        // The theme's <slideshow-component> custom element calls connectedCallback
        // automatically the moment it is attached to the live DOM, so no manual
        // initialisation is needed here.
      } else {
        currentElements.contentEl.innerHTML =
          '<div style="width:100%;text-align:center;padding:40px 0;">No products found in this promotion.</div>';
      }
    } catch (err) {
      console.error('[ProductPromoModal] fetch error:', err);
      const currentElements = this.getElements();
      if (currentElements && currentElements.contentEl) {
        currentElements.contentEl.innerHTML =
          '<div style="width:100%;text-align:center;padding:40px 0;">Error loading products.</div>';
      }
    }
  }

  close() {
    const elements = this.getElements();
    if (!elements) return;

    const { modal, contentEl } = elements;
    if (!modal) return;
    modal.style.display = 'none';
    document.body.style.overflow = '';

    // Clear content so next open() always fetches fresh
    if (contentEl) contentEl.innerHTML = '';
  }
}

// Initialise once
if (!window.productPromoModalInitialized) {
  window.productPromoModal = new ProductPromoModal();
  window.productPromoModalInitialized = true;
}
